import EyeIcon from "@/components/icons/eye";
import { HeaderCell } from "@/components/ui/table";
import { SortConfig } from "@/hooks/useTableServer";
import dayjs from "dayjs";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ActionIcon, Button, Modal, Text, Tooltip } from "rizzui";
import { IInvoice } from "@/utilities/types/Invoice";
import formatPhone from "@/utilities/functions/format-phone";
import { FaExternalLinkAlt } from "react-icons/fa";
import { ColumnsType } from "rc-table";

type InvoiceSortBy =
  | "id"
  | "originPrice"
  | "discountPrice"
  | "totalTax"
  | "total"
  | "user"
  | "createdAt";

type Columns = {
  data: IInvoice[];
  sortConfig?: SortConfig<InvoiceSortBy>;
  onHeaderCellClick?: (value: string) => void;
  handleReset: () => void;
  refresh?: () => void;
};

// Invoice Details Modal Component
const InvoiceDetailsModal = ({
  isOpen,
  onClose,
  invoiceData,
}: {
  isOpen: boolean;
  onClose: () => void;
  invoiceData: IInvoice;
}) => {
  const email = invoiceData?.xeroPayment?.user?.email;
  const phone = invoiceData?.xeroPayment?.user?.phone;
  const firstName = invoiceData?.xeroPayment?.user?.firstName || "";
  const lastName = invoiceData?.xeroPayment?.user?.lastName || "";
  const fullName =
    invoiceData?.xeroPayment?.user?.firstName ||
    invoiceData?.xeroPayment?.user?.lastName
      ? `${firstName} ${lastName}`
      : "";
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Invoice Details</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <h3 className="font-semibold text-lg">
              Xero ID: {invoiceData.xeroPayment?.xeroInvoiceID}
            </h3>
            {/* <p className="text-gray-500">
              {invoiceData.description || "No description"}
            </p> */}
          </div>

          <div className="border-t pt-4 mt-2 col-span-2">
            <div className="grid grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-gray-500">Origin</p>
                <p className="font-medium">{invoiceData.originPrice || 0}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Discount</p>
                <p className="font-medium">{invoiceData.discountPrice || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Tax</p>
                <p className="font-medium">{invoiceData.totalTax || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="font-medium">{invoiceData.total || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created At</p>
                <p className="font-medium">
                  {invoiceData.createdAt
                    ? dayjs(invoiceData.createdAt).format("MMMM DD, YYYY")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">User</p>
                {email && <p className="font-medium"> {email}</p>}
                {fullName && <p className="font-medium"> {fullName}</p>}
                {phone && <p className="font-medium"> {formatPhone(phone)}</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Simple view button component with its own state
const InvoiceViewButton = ({ invoiceData }: { invoiceData: IInvoice }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <Tooltip
        size="sm"
        content={"View Invoice"}
        placement="top"
        color="invert"
      >
        <ActionIcon size="sm" variant="outline" onClick={openModal}>
          <EyeIcon className="h-4 w-4" />
        </ActionIcon>
      </Tooltip>

      {isModalOpen && (
        <InvoiceDetailsModal
          isOpen={isModalOpen}
          onClose={closeModal}
          invoiceData={invoiceData}
        />
      )}
    </>
  );
};

export const getColumns = ({
  sortConfig,
  onHeaderCellClick,
}: Columns): ColumnsType<IInvoice> => {
  console.log("sortConfig", sortConfig);
  return [
    {
      title: (
        <HeaderCell
          title="Xero Invoice ID"
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "id"
          }
        />
      ),
      dataIndex: "id",
      key: "id",
      // @ts-expect-error - The ControlledTable component expects a different column type
      onHeaderCell: () => onHeaderCellClick?.("id"),
      render: (_: string, row: IInvoice) => {
        return (
          <span className="font-medium">{row?.xeroPayment?.xeroInvoiceID}</span>
        );
      },
    },
    {
      title: (
        <HeaderCell
          title="Origin"
          sortable
          align="right"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "originPrice"
          }
        />
      ),
      dataIndex: "originPrice",
      key: "originPrice",
      align: "center",
      // @ts-expect-error - The ControlledTable component expects a different column type
      onHeaderCell: () => onHeaderCellClick?.("originPrice"),
      render: (_: string, row: IInvoice) => {
        return <span>{row?.originPrice || 0}</span>;
      },
    },
    {
      title: (
        <HeaderCell
          title="Discount"
          sortable
          align="right"
          ascending={
            sortConfig?.direction === "asc" &&
            sortConfig?.key === "discountPrice"
          }
        />
      ),
      dataIndex: "discountPrice",
      key: "discountPrice",
      align: "center",
      // @ts-expect-error - The ControlledTable component expects a different column type
      onHeaderCell: () => onHeaderCellClick?.("discountPrice"),
      render: (_: string, row: IInvoice) => {
        return <span>{row?.discountPrice || 0}</span>;
      },
    },
    // {
    //   title: (
    //     <HeaderCell
    //       title="Tax"
    //       sortable
    //       align="right"
    //       ascending={
    //         sortConfig?.direction === "asc" && sortConfig?.key === "totalTax"
    //       }
    //     />
    //   ),
    //   dataIndex: "totalTax",
    //   key: "totalTax",
    //   align: "right",
    //   // @ts-expect-error - The ControlledTable component expects a different column type
    //   onHeaderCell: () => onHeaderCellClick?.("totalTax"),
    //   render: (_: string, row: IInvoice) => {
    //     return <span>{row?.totalTax || 0}</span>;
    //   },
    // },
    {
      title: (
        <HeaderCell
          title="Total"
          sortable
          align="right"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "total"
          }
        />
      ),
      dataIndex: "total",
      key: "total",
      align: "center",
      // @ts-expect-error - The ControlledTable component expects a different column type
      onHeaderCell: () => onHeaderCellClick?.("total"),
      render: (_: string, row: IInvoice) => {
        return <span>{row?.total || 0}</span>;
      },
    },
    {
      title: (
        <HeaderCell
          title="User"
          sortable
          align="center"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "user"
          }
        />
      ),
      dataIndex: "user",
      key: "user",
      align: "center",
      // @ts-expect-error - The ControlledTable component expects a different column type
      onHeaderCell: () => onHeaderCellClick?.("user"),
      render: (_: string, row: IInvoice) => {
        const email = row?.xeroPayment?.user?.email;
        const phone = row?.xeroPayment?.user?.phone;
        const firstName = row?.xeroPayment?.user?.firstName || "";
        const lastName = row?.xeroPayment?.user?.lastName || "";
        const fullName =
          row?.xeroPayment?.user?.firstName || row?.xeroPayment?.user?.lastName
            ? `${firstName} ${lastName}`
            : "";
        return (
          <div>
            {email && (
              <Text className="text-sm font-medium text-gray-900 dark:text-gray-700">
                {email}
              </Text>
            )}

            {fullName && (
              <Text className="text-sm font-medium text-gray-900 dark:text-gray-700">
                {fullName}
              </Text>
            )}

            {phone && (
              <Text className="text-[13px] text-gray-500">
                {formatPhone(phone)}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <HeaderCell
          title="Created At"
          sortable
          align="center"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "createdAt"
          }
        />
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      // @ts-expect-error - The ControlledTable component expects a different column type
      onHeaderCell: () => onHeaderCellClick?.("createdAt"),
      render: (date: string) => {
        return date ? dayjs(date).format("MMMM DD, YYYY") : "-";
      },
    },
    {
      title: (
        <HeaderCell
          className="ps-3"
          title={<span className="whitespace-nowrap">Actions</span>}
        />
      ),
      dataIndex: "action",
      key: "action",
      render: (_: string, row: IInvoice) => {
        return (
          <div className="flex items-center justify-end gap-3 pe-4">
            <InvoiceViewButton invoiceData={row} />

            {row?.xeroPayment?.onlineInvoiceUrl && (
              <Tooltip
                size="sm"
                content={"Detail payment"}
                placement="top"
                color="invert"
              >
                <Link to={row?.xeroPayment?.onlineInvoiceUrl} target="_blank">
                  <ActionIcon size="sm" variant="outline">
                    <FaExternalLinkAlt className="h-4 w-4" />
                  </ActionIcon>
                </Link>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];
};
